import * as React from 'react';

export type Theme =
  | 'light'
  | 'dark'
  | 'paper'
  | 'rose'
  | 'lavender'
  | 'peach'
  | 'mauve'
  | 'slate'
  | 'forest'
  | 'midnight'
  | 'shiba'
  | 'cat'
  | 'ocean'
  | 'sky'
  | 'mint'
  | 'matcha'
  | 'amber'
  | 'mocha'
  | 'crimson'
  | 'nord'
  | 'dracula'
  | 'graphite';

/** 预览色块：picker 卡片用三段颜色画一个迷你预览，无需为每个主题写条件分支 */
export interface ThemeSwatch {
  bg: string;
  bar1: string;
  bar2: string;
}

export interface ThemeMeta {
  value: Theme;
  /** 暗色主题：colorScheme = dark，且参与 Tailwind 的 dark: variant */
  dark?: boolean;
  swatch: ThemeSwatch;
}

// label / description 走 i18n key（theme.<value>），组件渲染时通过 t() 翻译
export const THEMES: ThemeMeta[] = [
  { value: 'light', swatch: { bg: '#ffffff', bar1: '#e5e5e5', bar2: '#3b82f6' } },
  { value: 'dark', dark: true, swatch: { bg: '#171717', bar1: '#404040', bar2: '#60a5fa' } },
  { value: 'paper', swatch: { bg: '#f7f1e1', bar1: '#e9d8a6', bar2: '#b45309' } },
  { value: 'rose', swatch: { bg: '#fff1f5', bar1: '#fbcfe0', bar2: '#e11d62' } },
  { value: 'lavender', swatch: { bg: '#f6f3fe', bar1: '#ddd0f7', bar2: '#7c3aed' } },
  { value: 'peach', swatch: { bg: '#fff4ee', bar1: '#ffd9c2', bar2: '#ea6a3a' } },
  { value: 'mauve', dark: true, swatch: { bg: '#1f1620', bar1: '#3d2f3e', bar2: '#f472b6' } },
  { value: 'slate', swatch: { bg: '#f4f5f7', bar1: '#d6dae1', bar2: '#475569' } },
  { value: 'forest', swatch: { bg: '#f0f4ee', bar1: '#cfe0cc', bar2: '#2f7d57' } },
  { value: 'midnight', dark: true, swatch: { bg: '#0f172a', bar1: '#27324d', bar2: '#60a5fa' } },
  { value: 'shiba', swatch: { bg: '#faf2e2', bar1: '#ecd9b6', bar2: '#c2622d' } },
  { value: 'cat', swatch: { bg: '#f4f1ec', bar1: '#e0d8cd', bar2: '#d97a86' } },
  { value: 'ocean', swatch: { bg: '#eef8fb', bar1: '#c3e3ec', bar2: '#0e7490' } },
  { value: 'sky', swatch: { bg: '#eef3fe', bar1: '#c5d6f7', bar2: '#2563eb' } },
  { value: 'mint', swatch: { bg: '#ecfaf4', bar1: '#b8e6d5', bar2: '#0d9488' } },
  { value: 'matcha', swatch: { bg: '#f3f6e6', bar1: '#d4dea6', bar2: '#5a8a1f' } },
  { value: 'amber', swatch: { bg: '#fdf6e3', bar1: '#f0dca0', bar2: '#b8860b' } },
  { value: 'mocha', swatch: { bg: '#f3ebe2', bar1: '#d8c4ac', bar2: '#8a5a2b' } },
  { value: 'crimson', swatch: { bg: '#fdf2f2', bar1: '#f6c9c9', bar2: '#dc2626' } },
  { value: 'nord', dark: true, swatch: { bg: '#2e3440', bar1: '#434c5e', bar2: '#88c0d0' } },
  { value: 'dracula', dark: true, swatch: { bg: '#282a36', bar1: '#44475a', bar2: '#bd93f9' } },
  { value: 'graphite', dark: true, swatch: { bg: '#1a1a1a', bar1: '#3a3a3a', bar2: '#d4d4d4' } },
];

const THEME_VALUES = THEMES.map((t) => t.value);
const DARK_THEMES = new Set(THEMES.filter((t) => t.dark).map((t) => t.value));

export function isTheme(v: unknown): v is Theme {
  return typeof v === 'string' && (THEME_VALUES as string[]).includes(v);
}

const STORAGE_KEY = 'doneline-theme-v1';

interface Ctx {
  theme: Theme;
  setTheme: (t: Theme) => void;
}

const ThemeContext = React.createContext<Ctx | null>(null);

function applyTheme(t: Theme) {
  const root = document.documentElement;
  root.classList.remove(...THEME_VALUES);
  root.classList.add(t);
  root.dataset.theme = t;
  root.style.colorScheme = DARK_THEMES.has(t) ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = React.useState<Theme>('light');

  // initial mount: read localStorage (or system preference)
  React.useEffect(() => {
    const stored = (typeof window !== 'undefined' &&
      localStorage.getItem(STORAGE_KEY)) as Theme | null;
    let initial: Theme = 'light';
    if (isTheme(stored)) {
      initial = stored;
    } else if (
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-color-scheme: dark)').matches
    ) {
      initial = 'dark';
    }
    applyTheme(initial);
    setThemeState(initial);
  }, []);

  const setTheme = React.useCallback((t: Theme) => {
    applyTheme(t);
    localStorage.setItem(STORAGE_KEY, t);
    setThemeState(t);
  }, []);

  const value = React.useMemo(() => ({ theme, setTheme }), [theme, setTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
}
